import { useCallback, useEffect, useState } from 'react';
import { z } from '@framework/zod';
import Modal from '@creditflowresearch/uilib/Modal';
import Button from '@creditflowresearch/uilib/Button';
import Flex from '@creditflowresearch/uilib/Flex';
import Input from '@creditflowresearch/uilib/Input';

import styles from './BondScreenerViewNameModal.module.scss';

const MAX_VIEW_NAME_LENGTH = 64;
const MIN_VIEW_NAME_LENGTH = 2;

const buildViewNameSchema = (existingNames: string[]) => z.string().trim().min(
  MIN_VIEW_NAME_LENGTH,
  `View name must be at least ${ MIN_VIEW_NAME_LENGTH } characters`,
).refine(
  (value) => !existingNames.some((existingName) => existingName.toLowerCase() === value.toLowerCase()),
  'View name already exists',
);

interface IBondScreenerViewNameModalProps {
  isVisible: boolean,
  title: string,
  submitLabel: string,
  dataTest: string,
  existingNames: string[],
  initialName?: string,
  onSubmit: (name: string) => void,
  onCancel: () => void,
}

const BondScreenerViewNameModal = ({
  isVisible,
  title,
  submitLabel,
  dataTest,
  existingNames,
  initialName = '',
  onSubmit,
  onCancel,
}: IBondScreenerViewNameModalProps) => {
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (isVisible) {
      setName(initialName);
    }
  }, [isVisible, initialName]);

  const validation = buildViewNameSchema(existingNames).safeParse(name);
  const error = validation.success ? undefined : validation.error.issues[0].message;
  const isUnchanged = name.trim().toLowerCase() === initialName.trim().toLowerCase();
  const canSubmit = validation.success && !isUnchanged;

  const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement> | string): void => {
    setName(typeof event === 'string' ? event : event.target.value);
  }, []);

  const handleSubmit = useCallback((): void => {
    if (!canSubmit || !validation.success) {
      return;
    }

    onSubmit(validation.data);
  }, [canSubmit, validation, onSubmit]);

  const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  return (
    <Modal
      isVisible={ isVisible }
      onCloseClicked={ onCancel }
      hideOnOverlayClicked
      title={ title }
      dataTest={ dataTest }
      className={ styles.modal }
      footer={ (
        <Flex gap={ 30 } align="center">
          <Button
            variant="solid"
            size="M"
            onClick={ handleSubmit }
            disabled={ !canSubmit }
            dataTest={ `${ dataTest }Submit` }
          >
            { submitLabel }
          </Button>
          <Button
            variant="text"
            size="M"
            onClick={ onCancel }
            dataTest={ `${ dataTest }Cancel` }
          >
            Cancel
          </Button>
        </Flex>
      ) }
    >
      <label className={ styles.label } htmlFor={ `${ dataTest }NameInput` }>Name</label>
      <Input
        name={ `${ dataTest }NameInput` }
        input={ {
          name: `${ dataTest }NameInput`,
          value: name,
          onChange: handleNameChange,
          onBlur: () => {},
          onFocus: () => {},
        } }
        meta={ { touched: name.length > 0, error } }
        onKeyPress={ handleKeyPress }
        placeholder="Enter view name"
        maxLength={ MAX_VIEW_NAME_LENGTH }
        isNarrow
        autoFocus
        dataTest={ `${ dataTest }NameInput` }
      />
    </Modal>
  );
};

export default BondScreenerViewNameModal;
